import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { showSuccess } from '../utils/notification';
import {
  HomeIcon, ClockIcon, UserCircleIcon, CogIcon, BellIcon, Bars3Icon, XMarkIcon, MoonIcon, SunIcon,
  ShieldCheckIcon, ArrowRightOnRectangleIcon,
  HeartIcon
} from '@heroicons/react/24/outline';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Jobs', href: '/jobs', icon: ClockIcon },
  { name: 'Profile', href: '/profile', icon: UserCircleIcon },
  { name: 'Settings', href: '/settings', icon: CogIcon },
];

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { isAuthenticated, logout, loading, user } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    navigate('/login', { replace: true });
    return null;
  }

  const handleLogout = () => {
    logout();
    showSuccess('Logged out successfully.');
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen font-sans">
      {/* Sidebar */}
      <aside className={`z-40 fixed inset-y-0 left-0 w-64 sidebar flex flex-col transition-transform duration-200 lg:static lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="h-16 flex items-center justify-between px-6 border-b border-gray-200 bg-white">
          <span className="text-xl font-bold text-primary-600">SimpleCronJobs</span>
          <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
            <XMarkIcon className="w-6 h-6 text-gray-400" />
          </button>
        </div>
        <nav className="flex-1 px-4 py-6 space-y-2">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                location.pathname === item.href
                  ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-200'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              onClick={() => setSidebarOpen(false)}
            >
              <item.icon className="w-5 h-5 mr-3" />
              {item.name}
            </Link>
          ))}
          {user?.isAdmin && (
            <>
              <Link
                to="/admin"
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                  location.pathname.startsWith('/admin') && !location.pathname.startsWith('/admin/health')
                    ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-200'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <ShieldCheckIcon className="w-5 h-5 mr-3" />
                Admin Panel
              </Link>
              <Link
                to="/admin/health"
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                  location.pathname === '/admin/health'
                    ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-200'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <HeartIcon className="w-5 h-5 mr-3" />
                System Health
              </Link>
            </>
          )}
          {Array.isArray(user?.roles) && user.roles.includes('reseller') && (
            <Link
              to="/reseller-dashboard"
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                location.pathname === '/reseller-dashboard'
                  ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-200'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              onClick={() => setSidebarOpen(false)}
            >
              <ShieldCheckIcon className="w-5 h-5 mr-3" />
              Reseller Dashboard
            </Link>
          )}
          <button
            onClick={() => {
              handleLogout();
              setSidebarOpen(false);
            }}
            className="flex w-full items-center px-4 py-2 rounded-lg transition-colors text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-gray-700"
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5 mr-3" />
            Logout
          </button>
        </nav>
      </aside>
      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Topbar */}
        <header className="h-16 topbar flex items-center justify-between px-6">
          <div className="flex items-center space-x-4">
            <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
              <Bars3Icon className="w-6 h-6 text-gray-500 dark:text-gray-300" />
            </button>
            <span className="hidden lg:block text-lg font-semibold text-primary-700 dark:text-primary-200">Dashboard</span>
          </div>
          <div className="flex items-center space-x-4">
            <button className="relative">
              <BellIcon className="w-6 h-6 text-gray-400 dark:text-gray-300" />
            </button>
            <button onClick={toggleTheme} className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
              {theme === 'dark' ? (
                <SunIcon className="w-5 h-5 text-yellow-400" />
              ) : (
                <MoonIcon className="w-5 h-5 text-gray-600" />
              )}
            </button>
          </div>
        </header>
        <main className="flex-1 p-6 lg:p-8 main-content">{children}</main>
      </div>
    </div>
  );
};

export default Layout; 